import type {
    AnySceneObjectT,
    AutoFrameI,
    CanvasI,
    EllipseI,
    LineI,
    FlexFrameI,
    FrameI,
    IconI,
    ImageI,
    ItemI,
    SequenceI,
    SvgI,
    TextI,
    TickerI,
    VideoPanel,
} from "tweenly"
import { initializeGroupStyles } from "tweenly"
import { v4 as uuidv4 } from "uuid"
import { isImageEditable, isTextEditable, textSizing } from "./objects.attributes.defaults"
import {
    defaultAutoFrameStyle,
    defaultEllipseStyle,
    defaultLineStyle,
    defaultFlexFrameStyle,
    defaultGroupFills,
    defaultGroupStyle,
    defaultIconStyle,
    defaultImageFills,
    defaultImageStyle,
    defaultImageTransforms,
    defaultItemFills,
    defaultItemStyle,
    defaultItemTransforms,
    defaultScrollTickerTextStyle,
    defaultTextStyle,
    defaultTextTransforms,
    defaultTickerCrawlTextStyle,
    defaultTickerCrawlTextWrapperItemStyle,
    defaultTickerFills,
    defaultTickerScrollTextWrapperItemStyle,
    defaultTickerStyle,
    defaultTickerTextWrapperItemFills,
    defaultVideoPanelStyle,
    defaultVideoPanelTransforms,
} from "./objects.styles.defaults"

export const defaultCanvas: CanvasI = {
    id: "",
    type: "canvas",
    width: 1920,
    height: 1080,
}

export const defaultItem: ItemI = {
    id: "",
    index: 0,
    title: "Rectangle",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "item",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultItemStyle,
    fill: defaultItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}

export const defaultEllipse: EllipseI = {
    id: "",
    index: 0,
    title: "Ellipse",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "ellipse",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultEllipseStyle,
    fill: defaultItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}

export const defaultLine: LineI = {
    id: "",
    index: 0,
    title: "Line",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "line",
    startPoint: "none",
    endPoint: "none",
    strokeAlign: "center",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [],
    styles: defaultLineStyle,
    fill: [],
    filters: [],
    transforms: defaultItemTransforms,
}

export const defaultSvg: SvgI = {
    id: "",
    index: 0,
    title: "SVG Shape",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "svg",
    svgType: "path",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [],
    styles: defaultItemStyle,
    fill: defaultItemFills,
    filters: [],
    transforms: defaultItemTransforms,
    svgContent: "",
    viewBox: undefined,
}

export const defaultIcon: IconI = {
    id: "",
    index: 0,
    title: "Icon",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "icon",
    iconName: "",
    iconFamily: "lucide",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [],
    styles: defaultIconStyle,
    fill: [],
    filters: [],
    transforms: defaultItemTransforms,
}

export const defaultAutoFrame: AutoFrameI = {
    id: "",
    index: 0,
    title: "Auto Frame",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "auto-frame",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultAutoFrameStyle,
    fill: defaultItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}

export const defaultFlexFrame: FlexFrameI = {
    id: "",
    index: 0,
    title: "Flex Frame",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "flex-frame",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    // Layout properties
    widthMode: "hug",
    heightMode: "hug",
    fixedWidth: undefined,
    fixedHeight: undefined,
    layout: "horizontal",
    gap: 0,
    padding: [0, 0, 0, 0],
    justifyContent: "start",
    alignItems: "start",
    cornerRadius: 0,
    clip: false,
    // Standard properties
    styles: defaultFlexFrameStyle,
    fill: defaultItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}

export const defaultFrame: FrameI = {
    id: "",
    index: 0,
    title: "Frame",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "frame",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [],
    styles: defaultItemStyle,
    fill: defaultItemFills,
    filters: [],
    transforms: defaultItemTransforms,
    // Frame-specific properties
    layout: "horizontal",
    gap: 0,
    padding: 0,
    justifyContent: "start",
    alignItems: "start",
    clip: false,
    layoutIncludeStroke: undefined,
}

type VideoPanelArgs = {
    width: number
    height: number
}

export const defaultVideoPanel = ({ width, height }: VideoPanelArgs): VideoPanel => {
    const id = "i" + uuidv4()

    return {
        id,
        index: -1,
        title: "Video Panel",
        isHidden: false,
        isLocked: false,
        isExpanded: true,
        isGroup: false,
        type: "video-panel",
        parentId: null,
        childIds: [],
        animations: {},
        attributes: [],
        styles: defaultVideoPanelStyle(width, height),
        fill: [],
        filters: [],
        transforms: initializeGroupStyles(id, defaultVideoPanelTransforms),
    }
}
type GroupWrapperArgs = {
    width: number
    height: number
    top: number
    left: number
}

export const defaultGroupWrapper = ({
    width,
    height,
    top,
    left,
}: GroupWrapperArgs): AnySceneObjectT => {
    const id = "i" + uuidv4()

    return {
        id,
        index: 0,
        title: "Wrapper",
        isHidden: false,
        isLocked: false,
        isExpanded: true,
        isGroup: true,
        type: "item",
        parentId: null,
        childIds: [],
        animations: {},
        attributes: [],
        styles: defaultGroupStyle(width, height, top, left),
        fill: defaultGroupFills,
        filters: [],
        transforms: defaultItemTransforms,
    }
}

export const defaultText: TextI = {
    id: "",
    index: 0,
    title: "Text",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "text",
    text: "Lorem ipsum",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isTextEditable, textSizing],
    styles: defaultTextStyle,
    fill: [],
    filters: [],
    transforms: defaultTextTransforms,
}

export const defaultImage: ImageI = {
    id: "",
    index: 0,
    title: "Image",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "image",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultImageStyle,
    fill: defaultImageFills,
    filters: [],
    transforms: defaultImageTransforms,
}

export const defaultSequence: SequenceI = {
    id: "",
    index: 0,
    title: "Image sequence",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "sequence",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultImageStyle,
    fill: defaultImageFills,
    filters: [],
    transforms: defaultImageTransforms,
}

export const defaultCrawlContinuousTicker: TickerI = {
    id: "",
    index: 0,
    title: "Ticker",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "crawl-continuous",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerStyle,
    fill: defaultTickerFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultCrawlStaggeredTicker: TickerI = {
    id: "",
    index: 0,
    title: "Ticker",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "crawl-staggered",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerStyle,
    fill: defaultTickerFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultTickerCrawlStaggeredTextWrapperItem: TickerI = {
    id: "",
    index: 0,
    title: "Text wrapper",
    isHidden: false,
    isLocked: true,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "crawl-staggered",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerCrawlTextWrapperItemStyle,
    fill: defaultTickerTextWrapperItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultTickerCrawlContinuousTextWrapperItem: TickerI = {
    id: "",
    index: 0,
    title: "Text wrapper",
    isHidden: false,
    isLocked: true,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "crawl-continuous",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerCrawlTextWrapperItemStyle,
    fill: defaultTickerTextWrapperItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultScrollUpTicker: TickerI = {
    id: "",
    index: 0,
    title: "Ticker",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "scroll-up",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerStyle,
    fill: defaultTickerFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultScrollUpDownTicker: TickerI = {
    id: "",
    index: 0,
    title: "Ticker",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "scroll-up-down",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerStyle,
    fill: defaultTickerFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultScrollDownTicker: TickerI = {
    id: "",
    index: 0,
    title: "Ticker",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "scroll-down",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerStyle,
    fill: defaultTickerFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultTickerScrollUpTextWrapperItem: TickerI = {
    id: "",
    index: 0,
    title: "Text wrapper",
    isHidden: false,
    isLocked: true,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "scroll-up",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerScrollTextWrapperItemStyle,
    fill: defaultTickerTextWrapperItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultTickerScrollDownTextWrapperItem: TickerI = {
    id: "",
    index: 0,
    title: "Text wrapper",
    isHidden: false,
    isLocked: true,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "scroll-down",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerScrollTextWrapperItemStyle,
    fill: defaultTickerTextWrapperItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultTickerScrollUpDownTextWrapperItem: TickerI = {
    id: "",
    index: 0,
    title: "Text wrapper",
    isHidden: false,
    isLocked: true,
    isExpanded: true,
    isGroup: false,
    type: "ticker",
    tickerType: "scroll-up-down",
    duration: 10,
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isImageEditable],
    styles: defaultTickerScrollTextWrapperItemStyle,
    fill: defaultTickerTextWrapperItemFills,
    filters: [],
    transforms: defaultItemTransforms,
}
export const defaultScrollTickerText: TextI = {
    id: "",
    index: 0,
    title: "Text",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "text",
    text: "Lorem ipsum",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isTextEditable, textSizing],
    styles: defaultScrollTickerTextStyle,
    fill: [],
    filters: [],
    transforms: defaultTextTransforms,
}
export const defaultTickerCrawlText: TextI = {
    id: "",
    index: 0,
    title: "Text",
    isHidden: false,
    isLocked: false,
    isExpanded: true,
    isGroup: false,
    type: "text",
    text: "Lorem ipsum",
    parentId: "",
    childIds: [],
    animations: {},
    attributes: [isTextEditable, textSizing],
    styles: defaultTickerCrawlTextStyle,
    fill: [],
    filters: [],
    transforms: defaultTextTransforms,
}
